import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../button";

import MaintenanceWrapper from "./maintenance.styles";

import logo from "../../assets/logo.svg";

import { IMaintenanceProps } from "./maintenance.interfaces";

const AUTO_CHECK_INTERVAL = 15 * 1000;
const AUTO_RELOAD_COOLDOWN = 2 * 60 * 1000;
const AUTO_RELOAD_STORAGE_KEY = "maintenanceAutoReloadedAt";

// Any response below 500 means the API is answering (CORS included) again;
// 502/503/504 come from the gateway while the API is down or deploying.
const isApiReachable = async (apiUrl: string) => {
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: "{ __typename }" }),
      cache: "no-store",
    });

    return response.status < 500;
  } catch {
    return false;
  }
};

const getLastAutoReload = () => {
  try {
    return Number(sessionStorage.getItem(AUTO_RELOAD_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
};

const markAutoReload = () => {
  try {
    sessionStorage.setItem(AUTO_RELOAD_STORAGE_KEY, String(Date.now()));
  } catch {}
};

const Maintenance = ({ apiUrl }: IMaintenanceProps) => {
  const { t } = useTranslation();

  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);
  const [isStillUnreachable, setIsStillUnreachable] = useState(false);

  // If the probe succeeds but the app lands straight back here (e.g. one query
  // keeps timing out at the gateway), don't keep auto-reloading in a loop.
  const [canAutoReload] = useState(
    () => !!apiUrl && Date.now() - getLastAutoReload() > AUTO_RELOAD_COOLDOWN
  );

  const handleRetry = useCallback(async () => {
    setIsChecking(true);
    setIsStillUnreachable(false);

    if (!apiUrl || (await isApiReachable(apiUrl))) {
      window.location.reload();
      return;
    }

    setIsChecking(false);
    setIsStillUnreachable(true);
  }, [apiUrl]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!apiUrl || !canAutoReload || !isOnline) return;

    let isCancelled = false;

    const check = async () => {
      if ((await isApiReachable(apiUrl)) && !isCancelled) {
        markAutoReload();
        window.location.reload();
      }
    };

    check();

    const interval = window.setInterval(check, AUTO_CHECK_INTERVAL);

    return () => {
      isCancelled = true;
      window.clearInterval(interval);
    };
  }, [apiUrl, canAutoReload, isOnline]);

  return (
    <MaintenanceWrapper>
      <div className="maintenance-container">
        <img src={logo} alt="BuildingHub Logo" />

        <h1>
          {isOnline
            ? t("global.maintenancePageTitle")
            : t("global.maintenanceOfflineTitle")}
        </h1>

        <p>
          {isOnline
            ? t("global.maintenanceSubTitleTitle")
            : t("global.maintenanceOfflineDescription")}
          {canAutoReload ? ` ${t("global.maintenanceAutoReload")}` : null}
        </p>

        <Button
          $size="large"
          variant="primary"
          className="maintenance-retry"
          isLoading={isChecking}
          onClick={handleRetry}
        >
          {t("global.maintenanceRetryAction")}
        </Button>

        <p className="maintenance-status" role="status" aria-live="polite">
          {isStillUnreachable ? t("global.maintenanceStillUnreachable") : null}
        </p>

        <p>
          {t("global.maintenanceDescription")}{" "}
          <a
            href={`mailto:${t("global.supportEmail")}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("global.supportEmail")}
          </a>
          .
        </p>
      </div>
    </MaintenanceWrapper>
  );
};

export default Maintenance;
